import { useCallback, useState } from 'react';

import type { OpsManager, OpsManagerSummary } from '@/services/opsRota/types';
import styled from '@/styles';

import { ENDS_NEXT_DAY } from '../messages';
import { C } from '../Operations.styles';
import {
  crossesMidnight,
  END_PLACEHOLDER,
  ScheduleFormValues,
  START_PLACEHOLDER,
} from '../scheduleForm';

import ManagerMultiSelect from './ManagerMultiSelect';
import TimePicker from './TimePicker';

const Fields = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
});

const Times = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
});

// UI spec §9.1: side by side, gap 24. Wraps under each other on a narrow
// panel.
const TimeRow = styled('div', {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '16px 24px',
});

const Hint = styled('p', {
  margin: 0,
  fontSize: '14px',
  color: C.muted,
});

type Pick = 'start' | 'end' | 'managers' | null;

type Props = {
  /** Test-id stem: "ops" on the page, "opsRepeat" in the Repeat modal. */
  idPrefix: string;
  values: ScheduleFormValues;
  onChange: (values: ScheduleFormValues) => void;
  /** Active managers; undefined while loading. */
  roster?: OpsManager[];
  rosterError?: boolean;
  onRetryRoster?: () => void;
  /** The edited block's managers (names of deactivated ones). */
  assigned?: OpsManagerSummary[];
  disabled?: boolean;
  layer?: 'panel' | 'modal';
  /**
   * An empty roster's "Manage managers" link (the page only); gets the
   * manager field, for focus to return to.
   */
  onManageManagers?: (trigger: HTMLElement | null) => void;
};

// Start / End pickers and the manager multi-select. One dropdown open at a
// time (the prototype's single `pick`). Shared with the Repeat modal.
const ScheduleFields = ({
  idPrefix: p,
  values,
  onChange,
  roster,
  rosterError,
  onRetryRoster,
  assigned,
  disabled,
  layer,
  onManageManagers,
}: Props) => {
  const [pick, setPick] = useState<Pick>(null);
  const close = useCallback(() => setPick(null), []);
  const toggle = (which: Exclude<Pick, null>) =>
    setPick((cur) => (cur === which ? null : which));
  const set = (patch: Partial<ScheduleFormValues>) =>
    onChange({ ...values, ...patch });

  return (
    <Fields>
      <Times>
        <TimeRow>
          <TimePicker
            id={`${p}StartTime`}
            label="Start time"
            value={values.startTime}
            placeholder={START_PLACEHOLDER}
            open={pick === 'start'}
            onToggle={() => toggle('start')}
            onClose={close}
            onPick={(startTime) => {
              set({ startTime });
              close();
            }}
            disabled={disabled}
            layer={layer}
            testIds={{
              field: `${p}StartTimeField`,
              list: `${p}StartTimeList`,
              optionPrefix: `${p}TimeOption-`,
            }}
          />
          <TimePicker
            id={`${p}EndTime`}
            label="End time"
            value={values.endTime}
            placeholder={END_PLACEHOLDER}
            open={pick === 'end'}
            onToggle={() => toggle('end')}
            onClose={close}
            onPick={(endTime) => {
              set({ endTime });
              close();
            }}
            disabled={disabled}
            layer={layer}
            testIds={{
              field: `${p}EndTimeField`,
              list: `${p}EndTimeList`,
              optionPrefix: `${p}TimeOption-`,
            }}
          />
        </TimeRow>
        {crossesMidnight(values) && (
          <Hint data-testid={`${p}FormNextDay`}>{ENDS_NEXT_DAY}</Hint>
        )}
      </Times>
      <ManagerMultiSelect
        idPrefix={p}
        roster={roster}
        rosterError={rosterError}
        onRetry={onRetryRoster}
        assigned={assigned}
        selectedIds={values.managerIds}
        open={pick === 'managers'}
        onToggle={() => toggle('managers')}
        onClose={close}
        onChange={(managerIds) => set({ managerIds })}
        disabled={disabled}
        layer={layer}
        onManageManagers={
          onManageManagers
            ? (trigger) => {
                close();
                onManageManagers(trigger);
              }
            : undefined
        }
      />
    </Fields>
  );
};

export default ScheduleFields;
